import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/v2/components/ui/avatar";
import { Skeleton } from "@/v2/components/ui/skeleton";
import PageRoutes from "@/utils/constants";
import type { ScheduleGame } from "./api";
// Reuse the schedule page's robust datetime parser so the search results show
// the game time. The local strict-format parser below only accepted one exact
// shape and returned "" for the search feed's datetimes, so the time was blank.
import { formatGameTime } from "@/v2/pages/Events/date-utils";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

type ScheduleTableProps = {
  games: ScheduleGame[];
  isLoading: boolean;
  isError: boolean;
  limit?: number;
  /**
   * Phone-width layout for the mobile search overlay. The wide layout needs
   * 500px (date, away, home and venue side by side), so on a phone the home
   * team and venue were cut off behind a sideways scroll. Compact keeps the
   * date column and stacks away over home, with the venue underneath.
   */
  compact?: boolean;
};

function toTitleCase(str?: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatGameDate(dateStr?: string): string {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("MMM. DD") : "";
}

const cellText: React.CSSProperties = { fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 };

/** One team on a single line (logo, then "City Mascot"), wrapping if it must. */
function CompactTeam({ name, mascot, logo }: { name?: string; mascot?: string; logo?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
      <Avatar className="shrink-0" style={{ width: '24px', height: '24px' }}>
        {logo ? <AvatarImage src={logo} alt={name ?? ''} /> : null}
        <AvatarFallback style={{ fontSize: '10px' }}>{name?.[0] ?? '?'}</AvatarFallback>
      </Avatar>
      <p style={{ ...cellText, minWidth: 0, overflowWrap: 'anywhere' }}>
        {[toTitleCase(name), toTitleCase(mascot)].filter(Boolean).join(' ')}
      </p>
    </div>
  );
}

export function ScheduleTable({ games, isLoading, isError, limit = 5, compact = false }: ScheduleTableProps) {
  const navigate = useNavigate();

  const handleNavigate = (gameId: string | number) => {
    navigate(PageRoutes.Watch.replace(":gameId", String(gameId)));
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="mx-auto mb-2 h-8 w-full last:mb-0" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-destructive">Failed to load games.</p>
      </div>
    );
  }

  const visibleGames = games.slice(0, limit);

  return (
    <div className="v2-schedule-table-wrapper" style={{ border: '1px solid var(--v2-border)', borderRadius: '8px', overflowX: 'auto', backgroundColor: 'var(--v2-background)' }}>
      <table style={{ width: '100%', minWidth: compact ? undefined : '500px', borderCollapse: 'collapse' }}>
        <tbody>
          {visibleGames.length > 0 ? (
            visibleGames.map((row, index) => (
              <tr
                key={row.game_id}
                onClick={() => handleNavigate(row.game_id)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--v2-background)',
                  borderBottom: index < visibleGames.length - 1 ? '1px solid var(--v2-border)' : 'none',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-accent)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-background)')}
              >
                {/* Date + Time */}
                <td style={{ padding: '10px 8px 10px 12px', verticalAlign: 'middle', width: '95px', minWidth: '95px', whiteSpace: 'nowrap' }}>
                  <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                    {formatGameDate(row.game_date)}
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                    {formatGameTime(row.game_date_time)}
                  </p>
                </td>

                {compact ? (
                  <td style={{ padding: '10px 12px 10px 8px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {row.card_image_url_wide ? (
                        <img
                          src={row.card_image_url_wide}
                          alt={`Game ${row.game_id}`}
                          style={{ height: '40px', width: '100%', borderRadius: '6px', objectFit: 'contain' }}
                        />
                      ) : (
                        <>
                          <CompactTeam name={row.away_team} mascot={row.away_team_mascot} logo={row.away_team_logo} />
                          <CompactTeam name={row.home_team} mascot={row.home_team_mascot} logo={row.home_team_logo} />
                        </>
                      )}
                      {row.facility_name ? (
                        <p style={{ ...cellText, color: 'var(--v2-muted-foreground)' }}>
                          {toTitleCase(row.facility_name)}
                        </p>
                      ) : null}
                    </div>
                  </td>
                ) : row.card_image_url_wide ? (
                  <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '160px' }} colSpan={2}>
                    <img
                      src={row.card_image_url_wide}
                      alt={`Game ${row.game_id}`}
                      style={{ height: '46px', width: '100%', borderRadius: '6px', objectFit: 'contain' }}
                    />
                  </td>
                ) : (
                  <>
                    {/* Away Team */}
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar className="shrink-0" style={{ width: '36px', height: '36px' }}>
                          {row.away_team_logo ? <AvatarImage src={row.away_team_logo} alt={row.away_team ?? ''} /> : null}
                          <AvatarFallback style={{ fontSize: '10px' }}>{row.away_team?.[0] ?? '?'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.away_team)}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.away_team_mascot)}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Home Team */}
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar className="shrink-0" style={{ width: '36px', height: '36px' }}>
                          {row.home_team_logo ? <AvatarImage src={row.home_team_logo} alt={row.home_team ?? ''} /> : null}
                          <AvatarFallback style={{ fontSize: '10px' }}>{row.home_team?.[0] ?? '?'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.home_team)}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.home_team_mascot)}
                          </p>
                        </div>
                      </div>
                    </td>
                  </>
                )}

                {/* Venue (compact puts it under the teams instead) */}
                {!compact ? (
                  <td style={{ padding: '10px 12px 10px 8px', verticalAlign: 'middle' }}>
                    <p style={{ fontSize: '12px', color: 'var(--v2-muted-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0, maxWidth: '120px' }}>
                      {toTitleCase(row.facility_name)}
                    </p>
                  </td>
                ) : null}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={compact ? 2 : 4} style={{ padding: '16px', textAlign: 'center', verticalAlign: 'middle' }}>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>No games found</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

